import { asArray, sumOf } from './promoPrimitives';
import { additionalRowsOf, containersOf, findFreeRow, findMainRow, findMainStatedRow, getCampaign, getOffer, getOfferInfo, listedProductIds, negoRowsOf, newRow, statedRowsOf } from './promoModel';
import { autoContainer, toUC } from './promoUnits';
import { isProductEditable } from './promoNego';
import { isStatementBlocked } from './promoForecast';
import { priceOf } from './promoValues';

// Statement (constat) of the products of an offer during the store period (#23521). Port of `updateStatementHandler`
// (visitStep3-promo.js:2825-2987) and of the statement half of `applyPromoRules` (JS:882-910), plus the bulk presence
// actions and the target indicator of the spec (§5, §7). Functions return the full stated rows of the offer scope.
// The free line of a product is stated on its own row (`isFree`), without price nor additional presence (#23572).

export const PRESENCE = Object.freeze({ NONE: 0, PRESENT: 1, ABSENT: 2 });

const FIELDS = { volume: 'volume', container: 'productContainerId', location: 'locationPromoId', highlight: 'locationHighlightId', price: 'statedValue' };

/** A product not stated present keeps nothing but its presence (RG-01 : the statement zone stays greyed). */
const emptied = (row) => ({ ...row, volume: 0, volumeUC: 0, productContainerId: 0, locationPromoId: 0, locationHighlightId: 0, statedValue: 0 });

const sameProduct = (row, ref, productId) => row.campaignId === ref.campaignId && row.campaignOfferId === ref.campaignOfferId && row.productId === productId;

/** A volume, a price, a location, a highlight or an additional presence already stated. */
const hasData = (row) => row.additional || row.volume > 0 || row.statedValue > 0 || row.locationPromoId > 0 || row.locationHighlightId > 0;

/** The stated row of the paying line, or of the free line with `free`. */
const statedLineOf = (rows, ref, productId, free) => (free ? findFreeRow(rows, ref, productId) : findMainStatedRow(rows, ref, productId));

const newStatedRow = (ref, productId, free) => newRow(ref, productId, { offerKey: ref.offerKey || '', isFree: !!free });

const withoutAdditional = (rows, ref, productId) => {
	const additional = additionalRowsOf(rows, ref, productId);
	return rows.filter((row) => !additional.includes(row));
};

function finalize(snapshot, offer, row) {
	const next = { ...row };
	next.productContainerId = autoContainer(containersOf(snapshot, offer, row.productId), next.volume, next.productContainerId);
	next.volumeUC = toUC(snapshot, row.productId, next.volume, next.productContainerId);
	return next.present === PRESENCE.PRESENT ? next : emptied(next);
}

/** Sets a presence (not a toggle) : absent empties the line and its additional presences, none removes the statement. */
function setPresence(snapshot, offer, ref, rows, productId, present, free = false) {
	const existing = statedLineOf(rows, ref, productId, free);
	let next = rows;
	if (present !== PRESENCE.PRESENT && !free) next = withoutAdditional(next, ref, productId);
	if (present === PRESENCE.NONE) return existing ? next.filter((row) => row !== existing) : next;
	const main = finalize(snapshot, offer, { ...(existing || newStatedRow(ref, productId, free)), present });
	return existing ? next.map((row) => (row === existing ? main : row)) : [...next, main];
}

/**
 * Edits one statement field. `change` : `{ campaignId, campaignOfferId, offerKey, productId, free, field, value }`, field being
 * present (the clicked icon : clicking the selected one again un-states the product) | volume | container | location | highlight | price.
 * With `free`, the field is stated on the free line, which has no price.
 */
export function applyStatementChange(snapshot, change) {
	const campaign = getCampaign(snapshot, change.campaignId);
	const offer = getOffer(campaign, change.campaignOfferId);
	if (!campaign || !offer || (change.field !== 'present' && !FIELDS[change.field]) || (change.free && change.field === 'price')) return { ok: false, error: 'PromoUnknownTarget' };
	const ref = { campaignId: change.campaignId, campaignOfferId: change.campaignOfferId, offerKey: change.offerKey || null };
	if (!isProductEditable(snapshot, campaign, ref, change.productId)) return { ok: false, error: 'PromoReadonly' };
	if (isStatementBlocked(snapshot, campaign, ref)) return { ok: false, error: 'PromoForecastStatementBlocked' };

	const rows = statedRowsOf(snapshot, ref).map((row) => ({ ...row }));
	const existing = statedLineOf(rows, ref, change.productId, change.free);

	if (change.field === 'present') {
		const clicked = Number(change.value);
		const present = existing && existing.present === clicked ? PRESENCE.NONE : clicked;
		return { ok: true, ref, stated: setPresence(snapshot, offer, ref, rows, change.productId, present, change.free) };
	}

	const base = existing || newStatedRow(ref, change.productId, change.free);
	const main = finalize(snapshot, offer, { ...base, [FIELDS[change.field]]: Number(change.value) || 0 });
	return { ok: true, ref, stated: existing ? rows.map((row) => (row === existing ? main : row)) : [...rows, main] };
}

/**
 * "Tout présent" on a product not stated yet takes its negotiation over : quantity, unit, location and highlight of the
 * main line and of the additional presences, and the negotiated price (the reference shown under the price field).
 */
function takeNegotiated(snapshot, campaign, offer, ref, rows, productId) {
	if (rows.some((row) => sameProduct(row, ref, productId) && !row.isFree && hasData(row))) return rows;
	const nego = negoRowsOf(snapshot, ref).filter((row) => sameProduct(row, ref, productId) && !row.isFree && row.volume > 0);
	const negoMain = findMainRow(nego, ref, productId);
	if (!negoMain) return rows;
	const copy = (row) => ({ volume: row.volume, productContainerId: row.productContainerId, locationPromoId: row.locationPromoId, locationHighlightId: row.locationHighlightId });
	const existing = findMainStatedRow(rows, ref, productId);
	const main = finalize(snapshot, offer, { ...(existing || newRow(ref, productId, { offerKey: ref.offerKey || '' })), ...copy(negoMain), present: PRESENCE.PRESENT, statedValue: Number(negoMain.statedValue) || priceOf(campaign, productId) });
	const additional = nego.filter((row) => row.additional).map((row) => newRow(ref, productId, { offerKey: ref.offerKey || '', additional: true, ...copy(row), volumeUC: toUC(snapshot, productId, row.volume, row.productContainerId) }));
	const base = withoutAdditional(rows, ref, productId);
	return [...(existing ? base.map((row) => (row === existing ? main : row)) : [...base, main]), ...additional];
}

/** "Tout présent" on a free line not stated yet takes its free negotiation over : quantity, unit, location and highlight. */
function takeNegotiatedFree(snapshot, offer, ref, rows, productId) {
	const existing = findFreeRow(rows, ref, productId);
	const negoFree = findFreeRow(negoRowsOf(snapshot, ref), ref, productId);
	if (!existing || hasData(existing) || !(negoFree?.volume > 0)) return rows;
	const free = finalize(snapshot, offer, { ...existing, volume: negoFree.volume, productContainerId: negoFree.productContainerId, locationPromoId: negoFree.locationPromoId, locationHighlightId: negoFree.locationHighlightId });
	return rows.map((row) => (row === existing ? free : row));
}

const hasNegotiatedFree = (snapshot, ref, productId) => findFreeRow(negoRowsOf(snapshot, ref), ref, productId)?.volume > 0;

/**
 * Bulk actions "Tout présent" / "Tout absent" (PRESENCE.PRESENT / ABSENT) and "Réinitialiser" (PRESENCE.NONE) on every
 * listed product and on their negotiated free line (#23572). "Tout présent" keeps what is already stated and fills the
 * other lines from their negotiation.
 */
export function setAllPresence(snapshot, ref, present) {
	const campaign = getCampaign(snapshot, ref.campaignId);
	const offer = getOffer(campaign, ref.campaignOfferId);
	if (!campaign || !offer || snapshot?.readonly) return { ok: false, error: 'PromoReadonly' };
	if (isStatementBlocked(snapshot, campaign, ref)) return { ok: false, error: 'PromoForecastStatementBlocked' };
	let rows = statedRowsOf(snapshot, ref).map((row) => ({ ...row }));
	listedProductIds(snapshot, campaign, offer, getOfferInfo(snapshot, ref.offerKey)).forEach((productId) => {
		if (!isProductEditable(snapshot, campaign, ref, productId)) return;
		rows = setPresence(snapshot, offer, ref, rows, productId, present);
		if (present === PRESENCE.PRESENT) rows = takeNegotiated(snapshot, campaign, offer, ref, rows, productId);
		if (!hasNegotiatedFree(snapshot, ref, productId) && !findFreeRow(rows, ref, productId)) return;
		rows = setPresence(snapshot, offer, ref, rows, productId, present, true);
		if (present === PRESENCE.PRESENT) rows = takeNegotiatedFree(snapshot, offer, ref, rows, productId);
	});
	return { ok: true, ref, stated: rows };
}

/**
 * Target indicator of a product in the statement (spec §5) : negotiated UC (main line + additional presences, free
 * excluded) against stated UC (main line + additional presences, RG-05). `reached` when the stated volume covers it.
 * With `free`, the free line alone : its negotiated UC against its stated UC.
 */
export function productTarget(snapshot, ref, productId, free = false) {
	const nego = negoRowsOf(snapshot, ref).filter((row) => sameProduct(row, ref, productId) && !!row.isFree === free);
	const stated = statedRowsOf(snapshot, ref).filter((row) => sameProduct(row, ref, productId) && !!row.isFree === free);
	const targetUC = sumOf(nego, 'volumeUC') || 0;
	const statedUC = sumOf(stated, 'volumeUC') || 0;
	return { targetUC, statedUC, gap: statedUC - targetUC, reached: statedUC >= targetUC };
}

/** Negotiated conditions of a product (side panel, RG-04) : every negotiated presence with its UC, and their total. */
export function negotiatedConditions(snapshot, ref, productId) {
	const presences = negoRowsOf(snapshot, ref).filter((row) => sameProduct(row, ref, productId) && !row.isFree && (row.volume > 0 || row.additional));
	return { presences, totalUC: sumOf(presences, 'volumeUC') || 0, main: findMainRow(presences, ref, productId) };
}

/**
 * Summary of the negotiated presences of a product, for the "Négocié · 3 CAR" chip : the quantity of every presence in
 * their unit when they share it, otherwise their UC total (the main row alone hid the additional presences).
 */
export function negotiatedSummary(snapshot, ref, productId) {
	const { presences, totalUC } = negotiatedConditions(snapshot, ref, productId);
	if (!presences.length) return null;
	const containers = [...new Set(presences.map((row) => row.productContainerId || 0))];
	if (containers.length === 1 && containers[0]) return { volume: sumOf(presences, 'volume') || 0, containerId: containers[0], totalUC, count: presences.length };
	return { volume: null, containerId: null, totalUC, count: presences.length };
}

/** Whether "Tout absent" / "Réinitialiser" would wipe something already stated on the offer (a volume, a price, a location, a highlight or an additional presence). */
export function hasStatedData(snapshot, ref) {
	return statedRowsOf(snapshot, ref).some((row) => row.campaignId === ref.campaignId && row.campaignOfferId === ref.campaignOfferId && hasData(row));
}

export const statedRowOf = (snapshot, ref, productId, free = false) => statedLineOf(statedRowsOf(snapshot, ref), ref, productId, free);

export const statedAdditionalCount = (snapshot, ref, productId) => asArray(additionalRowsOf(statedRowsOf(snapshot, ref), ref, productId)).length;
